import type { NavigateToOptions } from '#app/composables/router';
import type { RuntimeConfig } from 'nuxt/schema';
import type { FetchError } from 'ofetch';

import * as Sentry from '@sentry/nuxt';

export enum ApiMethod {
  DELETE = 'DELETE',
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
}

export enum FilterTypeName {
  BOOLEAN = 'boolean',
  ENUM = 'enum',
  NUMBER = 'number',
  RANGE = 'range',
  STRING = 'string',
}

export interface ApiError {
  data: ApiErrorData;
  message: string;
  response: ApiErrorResponse;
}

export interface ApiErrorData {
  error: string;
  error_constraints?: Record<string, { code: string; message: string }[]>;
  error_description: string;
  message?: string | string[];
  statusCode?: number;
}

export interface ApiErrorResponse {
  status: number;
  statusText: string;
  url: string;
}

export interface ApiResponse<T, U> {
  data?: T;
  error?: ApiError;
  meta?: U;
}

export interface Attribute {
  count?: number;
  key: string;
  label?: string;
  type: AttributeType;
}

export interface FilterGroup {
  attributes: Attribute[];
  key: string;
  label: string;
}

export interface Meta {
  pagination?: Pagination;
}

export interface Pagination {
  count: number;
  limit: number;
  page: number;
  total: number;
}

interface AttributeType {
  name: FilterTypeName;
  values?: AttributeValue[];
}

interface AttributeValue {
  key: string;
  label: string;
}

interface AuthStoreType {
  $reset: () => void;
  accessToken: string | null;
  isLoggedIn: boolean;
  logout: () => Promise<void>;
}

interface CustomerStoreType {
  $reset: () => void;
}

export async function createApiClient<T, U = Meta>(
  method: ApiMethod,
  path: string,
  data: Record<string, unknown> | null | undefined,
  authenticated: boolean,
  goTo: (path: string, options?: NavigateToOptions) => ReturnType<typeof navigateTo>,
  runtimeConfig: RuntimeConfig,
  authStore: AuthStoreType,
  customerStore: CustomerStoreType,
): Promise<ApiResponse<T, U>> {
  await nextTick();

  const fetch = (accessToken: string | null): Promise<ApiResponse<T, U>> => {
    return $fetch<ApiResponse<T, U>>(path, {
      baseURL: runtimeConfig.public.bffBaseUrl,
      body: [ApiMethod.DELETE, ApiMethod.POST, ApiMethod.PUT].includes(method) ? data : null,
      headers: authenticated && accessToken ? { authorization: `Bearer ${accessToken}` } : {},
      method,
      params: [ApiMethod.GET].includes(method) && data ? data : undefined,
    });
  };

  return fetch(authStore.accessToken).catch((error: FetchError) => {
    const apiError: ApiError = {
      data: error.data || { error: 'Unknown error', error_description: error.message },
      message: error.message,
      response: {
        status: error.response?.status || 500,
        statusText: error.response?.statusText || 'Unknown Error',
        url: error.response?.url || path,
      },
    };

    if (error.response?.status && error.response.status >= 500) {
      Sentry.captureException(error, {
        extra: { responseData: error.data, url: path },
        tags: { apiMethod: method, statusCode: error.response.status },
      });
    }

    if (authStore.isLoggedIn && error?.response?.status === 401) {
      handleApiError('general.sessionExpired', 'warning', 'general.errorTitle', true);

      removeItemStorage(StorageKey.ACCESS_TOKEN);
      authStore.$reset();
      customerStore.$reset();

      goTo('/');
    }

    return { error: apiError };
  });
}

export const getErrorMessage = (error: ApiError | FetchError): string => {
  const errorData = 'data' in error ? error.data : error.data;

  if (!errorData) {
    return error.message || 'An unknown error occurred';
  }

  const { error_constraints, message, statusCode } = errorData as ApiErrorData;

  if (Array.isArray(message) && message.length > 0) {
    return message.join(', ');
  }

  if (error_constraints) {
    const firstConstraint = Object.values(error_constraints)[0]?.[0];
    if (firstConstraint?.message) {
      return firstConstraint.message;
    }
  }

  if (typeof message === 'string') {
    return message;
  }

  switch (statusCode) {
    case 400:
      return 'Invalid request';
    case 401:
      return 'Unauthorized';
    case 403:
      return 'Forbidden';
    case 404:
      return 'Not found';
    case 500:
      return 'Internal server error';
    default:
      return 'An unknown error occurred';
  }
};

export const handleApiError = async (
  error: string | FetchError,
  type: 'error' | 'neutral' | 'positive' | 'secondary' | 'warning' = 'error',
  title: string = 'general.errorTitle',
  translate: boolean = false,
): Promise<void> => {
  const text = typeof error === 'string' ? error : getErrorMessage(error);

  if (import.meta.client) {
    try {
      const { useAlertStore } = await import('~/stores/alert/alertStore');
      const alertStore = useAlertStore();
      alertStore.add({
        text,
        title,
        translate,
        type,
      });
    } catch (alertError) {
      console.warn('Failed to show alert message:', alertError);
    }
  } else console.error('API error occurred during Server-Side Rendering:', text);
};
